import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Building2,
  CalendarDays,
  Clock3,
  CornerDownLeft,
  FileText,
  FileSearch,
  LayoutDashboard,
  MessageCircle,
  MousePointer2,
  Package,
  Pin,
  PinOff,
  Search,
  Sparkles,
  Stethoscope,
  Trash2,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getVisibleNavItems } from "@/app/layouts/sidebarNavConfig";
import { useCapabilities } from "@/features/capabilities/hooks/useCapabilities";
import { getAuthenticatedSmartSearchResults } from "@/features/smart-search/api/smartSearchApi";
import type { SmartSearchResult } from "@/features/smart-search/types";
import { useAuthStore } from "@/lib/auth/authStore";
import { cn } from "@/lib/utils/cn";
import { Button, Input } from "@/shared/ui";

const RECENT_SEARCHES_STORAGE_KEY = "eshendetesia.smart_search.recent_searches";
const RECENTLY_VISITED_STORAGE_KEY = "eshendetesia.smart_search.recently_visited";
const PINNED_PAGES_STORAGE_KEY = "eshendetesia.smart_search.pinned_pages";
const MAX_RECENT_SEARCHES = 10;
const MAX_RECENTLY_VISITED = 8;
const BACKEND_SEARCH_MIN_QUERY_LENGTH = 2;
const BACKEND_SEARCH_DEBOUNCE_MS = 280;

interface PageSearchResult {
  id: string;
  label: string;
  route: string;
  icon: ReactNode;
}

type PaletteItemType = "favorite" | "recent_search" | "recent_page" | "page" | "backend";

interface PaletteItem {
  id: string;
  type: PaletteItemType;
  label: string;
  route?: string;
  icon: ReactNode;
  subtitle: string;
  routePreview?: string;
  shortcutHint?: string;
  pinned?: boolean;
  backendResult?: SmartSearchResult;
}

interface PaletteGroup {
  id: string;
  label: string;
  items: PaletteItem[];
}

interface StoredPage {
  id: string;
  label: string;
  route: string;
}

interface SmartSearchCommandPaletteProps {
  open: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  onClose: () => void;
}

export const SmartSearchCommandPalette = ({
  open,
  query,
  onQueryChange,
  onClose,
}: SmartSearchCommandPaletteProps): JSX.Element | null => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>(() =>
    readStringListFromStorage(RECENT_SEARCHES_STORAGE_KEY),
  );
  const [recentlyVisitedPages, setRecentlyVisitedPages] = useState<StoredPage[]>(() =>
    readStoredPagesFromStorage(RECENTLY_VISITED_STORAGE_KEY),
  );
  const [pinnedPages, setPinnedPages] = useState<StoredPage[]>(() =>
    readStoredPagesFromStorage(PINNED_PAGES_STORAGE_KEY),
  );
  const [backendResults, setBackendResults] = useState<SmartSearchResult[]>([]);
  const [backendSearchLoading, setBackendSearchLoading] = useState(false);
  const [backendSearchError, setBackendSearchError] = useState<string | null>(null);
  const { systemRole, profileRole, businessRole, activeFunctionalAccount } = useAuthStore();
  const capabilitiesQuery = useCapabilities();
  const currentLevel = capabilitiesQuery.data?.institution_level ?? 1;
  const trimmedQuery = query.trim();
  const normalizedQuery = trimmedQuery.toLowerCase();
  const isSearching = normalizedQuery.length > 0;
  const shouldSearchBackend = trimmedQuery.length >= BACKEND_SEARCH_MIN_QUERY_LENGTH;

  const visiblePageItems = useMemo<PageSearchResult[]>(() => {
    const visibleItems = getVisibleNavItems(
      systemRole,
      profileRole,
      businessRole,
      capabilitiesQuery.data,
      location.pathname,
      currentLevel,
      activeFunctionalAccount?.functional_account_type ?? null,
    );

    const uniqueItems = new Map<string, PageSearchResult>();
    visibleItems.forEach((item) => {
      uniqueItems.set(`${item.to}:${item.label}`, {
        id: `${item.to}:${item.label}`,
        label: item.label,
        route: item.to,
        icon: item.icon,
      });
    });

    return Array.from(uniqueItems.values());
  }, [
    activeFunctionalAccount?.functional_account_type,
    businessRole,
    capabilitiesQuery.data,
    currentLevel,
    location.pathname,
    profileRole,
    systemRole,
  ]);

  const visiblePageById = useMemo(() => {
    const pages = new Map<string, PageSearchResult>();
    visiblePageItems.forEach((page) => pages.set(page.id, page));
    return pages;
  }, [visiblePageItems]);

  const visibleRouteByPath = useMemo(() => {
    const pages = new Map<string, PageSearchResult>();
    visiblePageItems.forEach((page) => {
      pages.set(page.route, page);
      pages.set(page.route.split("?")[0] ?? page.route, page);
    });
    return pages;
  }, [visiblePageItems]);

  const pageResults = useMemo<PageSearchResult[]>(() => {
    if (!isSearching) {
      return visiblePageItems;
    }

    return visiblePageItems.filter((item) => {
      const haystack = `${item.label} ${item.route}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [isSearching, normalizedQuery, visiblePageItems]);

  useEffect(() => {
    if (!open || !shouldSearchBackend) {
      setBackendResults([]);
      setBackendSearchLoading(false);
      setBackendSearchError(null);
      return;
    }

    const abortController = new AbortController();
    const debounceTimer = window.setTimeout(() => {
      setBackendSearchLoading(true);
      setBackendSearchError(null);
      getAuthenticatedSmartSearchResults(trimmedQuery, abortController.signal)
        .then((response) => {
          if (!abortController.signal.aborted) {
            setBackendResults(response.results);
          }
        })
        .catch((error: unknown) => {
          if (!abortController.signal.aborted) {
            setBackendResults([]);
            setBackendSearchError(error instanceof Error ? error.message : "Smart Search nuk u ngarkua.");
          }
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setBackendSearchLoading(false);
          }
        });
    }, BACKEND_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(debounceTimer);
      abortController.abort();
    };
  }, [open, shouldSearchBackend, trimmedQuery]);

  const groups = useMemo<PaletteGroup[]>(() => {
    const pinnedItems = pinnedPages
      .map((storedPage) => storedPageToPaletteItem(storedPage, "favorite", visiblePageById.get(storedPage.id)))
      .filter((item) => itemMatchesQuery(item, normalizedQuery));

    const recentSearchItems = recentSearches
      .filter((recentSearch) => !normalizedQuery || recentSearch.toLowerCase().includes(normalizedQuery))
      .map<PaletteItem>((recentSearch) => ({
        id: `recent-search:${recentSearch}`,
        type: "recent_search",
        label: recentSearch,
        subtitle: "Kërkim i fundit",
        icon: <Clock3 className="h-4 w-4" aria-hidden="true" />,
        shortcutHint: "Tab",
      }));

    const recentlyVisitedItems = recentlyVisitedPages
      .map((storedPage) => storedPageToPaletteItem(storedPage, "recent_page", visiblePageById.get(storedPage.id)))
      .filter((item) => itemMatchesQuery(item, normalizedQuery));

    const pageItems = pageResults.map<PaletteItem>((page) => ({
      id: `page:${page.id}`,
      type: "page",
      label: page.label,
      route: page.route,
      subtitle: "Faqe",
      icon: page.icon,
      shortcutHint: "Enter",
      pinned: pinnedPages.some((pinnedPage) => pinnedPage.id === page.id),
    }));
    const backendGroups = buildBackendGroups(backendResults);

    return [
      { id: "favorites", label: "Favorites", items: pinnedItems },
      { id: "recent", label: "Recent", items: [...recentSearchItems, ...recentlyVisitedItems].slice(0, 12) },
      ...backendGroups.filter((group) => group.id === "backend-actions"),
      { id: "pages", label: "Pages", items: pageItems },
      ...backendGroups.filter((group) => group.id !== "backend-actions"),
    ].filter((group) => group.items.length > 0);
  }, [backendResults, normalizedQuery, pageResults, pinnedPages, recentSearches, recentlyVisitedPages, visiblePageById]);

  const selectableItems = useMemo(
    () => groups.flatMap((group) => group.items),
    [groups],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  useEffect(() => {
    const activePath = `${location.pathname}${location.search}`;
    const matchedPage = visibleRouteByPath.get(activePath) ?? visibleRouteByPath.get(location.pathname);
    if (!matchedPage) {
      return;
    }

    setRecentlyVisitedPages((currentPages) => {
      const nextPages = [
        { id: matchedPage.id, label: matchedPage.label, route: matchedPage.route },
        ...currentPages.filter((page) => page.id !== matchedPage.id),
      ].slice(0, MAX_RECENTLY_VISITED);
      writeStoredPagesToStorage(RECENTLY_VISITED_STORAGE_KEY, nextPages);
      return nextPages;
    });
  }, [location.pathname, location.search, visibleRouteByPath]);

  useEffect(() => {
    setActiveResultIndex(0);
  }, [normalizedQuery, selectableItems.length]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onDocumentKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  const hasResults = selectableItems.length > 0;
  const isLoading = capabilitiesQuery.isLoading || backendSearchLoading;

  const rememberSearch = (value: string): void => {
    const nextSearch = value.trim();
    if (!nextSearch) {
      return;
    }
    setRecentSearches((currentSearches) => {
      const nextSearches = [
        nextSearch,
        ...currentSearches.filter((storedSearch) => storedSearch.toLowerCase() !== nextSearch.toLowerCase()),
      ].slice(0, MAX_RECENT_SEARCHES);
      writeStringListToStorage(RECENT_SEARCHES_STORAGE_KEY, nextSearches);
      return nextSearches;
    });
  };

  const openItem = (item: PaletteItem): void => {
    if (item.type === "recent_search") {
      onQueryChange(item.label);
      rememberSearch(item.label);
      inputRef.current?.focus();
      return;
    }

    if (!item.route) {
      return;
    }

    rememberSearch(trimmedQuery);
    navigate(item.route);
    onClose();
  };

  const togglePinnedPage = (page: PageSearchResult | StoredPage): void => {
    setPinnedPages((currentPages) => {
      const exists = currentPages.some((storedPage) => storedPage.id === page.id);
      const nextPages = exists
        ? currentPages.filter((storedPage) => storedPage.id !== page.id)
        : [{ id: page.id, label: page.label, route: page.route }, ...currentPages].slice(0, 12);
      writeStoredPagesToStorage(PINNED_PAGES_STORAGE_KEY, nextPages);
      return nextPages;
    });
  };

  const removeRecentItem = (item: PaletteItem): void => {
    if (item.type === "recent_search") {
      setRecentSearches((currentSearches) => {
        const nextSearches = currentSearches.filter((storedSearch) => storedSearch !== item.label);
        writeStringListToStorage(RECENT_SEARCHES_STORAGE_KEY, nextSearches);
        return nextSearches;
      });
      return;
    }

    if (item.type === "recent_page") {
      const pageId = item.id.replace(/^recent-page:/, "");
      setRecentlyVisitedPages((currentPages) => {
        const nextPages = currentPages.filter((page) => page.id !== pageId);
        writeStoredPagesToStorage(RECENTLY_VISITED_STORAGE_KEY, nextPages);
        return nextPages;
      });
    }
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown" || event.key === "Tab") {
      event.preventDefault();
      setActiveResultIndex((current) => Math.min(current + 1, Math.max(selectableItems.length - 1, 0)));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveResultIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === "Enter" && selectableItems[activeResultIndex]) {
      event.preventDefault();
      openItem(selectableItems[activeResultIndex]);
    }
  };

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Smart Search">
      <button
        type="button"
        className="absolute inset-0 bg-background/70 backdrop-blur-sm"
        aria-label="Mbyll kërkimin"
        onClick={onClose}
      />
      <div className="fixed inset-x-0 top-0 flex h-[100dvh] flex-col overflow-hidden border-border bg-card text-card-foreground shadow-2xl sm:left-1/2 sm:right-auto sm:top-16 sm:h-auto sm:max-h-[min(82vh,760px)] sm:w-[min(780px,calc(100vw-32px))] sm:-translate-x-1/2 sm:rounded-[28px] sm:border">
        <div className="border-b border-border p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-semibold text-card-foreground">
                <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                Smart Search
              </p>
              <p className="truncate text-xs text-muted-foreground">Kërko brenda faqeve të lejuara për account-in aktiv.</p>
            </div>
            <Button type="button" size="icon" variant="ghost" aria-label="Mbyll kërkimin" onClick={onClose}>
              <X className="h-5 w-5" aria-hidden="true" />
            </Button>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              ref={inputRef}
              className="h-12 rounded-2xl pl-10 pr-24"
              placeholder="Kërko faqe, menu ose modul..."
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              onKeyDown={onInputKeyDown}
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-lg border border-border bg-secondary px-2 py-1 text-[11px] font-semibold text-muted-foreground sm:block">
              Ctrl K
            </kbd>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {isLoading ? <SmartSearchSkeleton /> : null}

          {!isLoading && hasResults ? (
            <div className="grid gap-4">
              {groups.map((group) => (
                <section key={group.id} aria-labelledby={`smart-search-${group.id}-heading`}>
                  <div className="mb-2 flex items-center justify-between px-2">
                    <h2
                      id={`smart-search-${group.id}-heading`}
                      className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground"
                    >
                      {group.label}
                    </h2>
                    <span className="text-xs text-muted-foreground">
                      {group.items.length > 0 ? group.items.length : "Soon"}
                    </span>
                  </div>
                  {group.items.length > 0 ? (
                    <div className="grid gap-1.5">
                      {group.items.map((item) => {
                        const absoluteIndex = selectableItems.findIndex((candidate) => candidate.id === item.id);
                        const active = absoluteIndex === activeResultIndex;
                        const route = item.route;
                        return (
                          <SmartSearchResultRow
                            key={`${group.id}:${item.id}`}
                            item={item}
                            active={active}
                            query={trimmedQuery}
                            onSelect={() => openItem(item)}
                            onHover={() => setActiveResultIndex(absoluteIndex)}
                            onTogglePin={
                              route && item.type !== "backend"
                                ? () => togglePinnedPage({ id: item.id.replace(/^(page|favorite|recent-page):/, ""), label: item.label, route })
                                : undefined
                            }
                            onRemoveRecent={
                              item.type === "recent_search" || item.type === "recent_page"
                                ? () => removeRecentItem(item)
                                : undefined
                            }
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border bg-secondary/20 px-3 py-4 text-xs text-muted-foreground">
                      Rezultatet nga modulet private do të lidhen në fazat e ardhshme.
                    </div>
                  )}
                </section>
              ))}
            </div>
          ) : null}

          {!isLoading && backendSearchError && shouldSearchBackend ? (
            <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
              {backendSearchError}
            </div>
          ) : null}

          {!isLoading && !hasResults ? <SmartSearchEmptyState /> : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-[11px] text-muted-foreground">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
              <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
              Navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <CornerDownLeft className="h-3.5 w-3.5" aria-hidden="true" />
              Open
            </span>
            <span>Esc Close</span>
          </div>
          <span className="inline-flex items-center gap-1">
            <MousePointer2 className="h-3.5 w-3.5" aria-hidden="true" />
            {selectableItems.length} results
          </span>
        </div>
      </div>
    </div>
  );
};

function SmartSearchResultRow({
  item,
  active,
  query,
  onSelect,
  onHover,
  onTogglePin,
  onRemoveRecent,
}: {
  item: PaletteItem;
  active: boolean;
  query: string;
  onSelect: () => void;
  onHover: () => void;
  onTogglePin?: () => void;
  onRemoveRecent?: () => void;
}): JSX.Element {
  return (
    <div
      className={cn(
        "group flex items-center gap-2 rounded-2xl p-1.5 transition",
        active ? "bg-secondary text-foreground" : "hover:bg-secondary/70",
      )}
      onMouseEnter={onHover}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-2 py-2 text-left focus:outline-none focus:ring-2 focus:ring-primary/40"
        aria-label={`Hap ${item.label}`}
        onClick={onSelect}
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
          {item.icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-card-foreground">
            <HighlightedText value={item.label} query={query} />
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {item.subtitle}
            {item.routePreview ? ` · ${item.routePreview}` : ""}
          </span>
        </span>
        {item.shortcutHint ? (
          <kbd className="hidden rounded-lg border border-border bg-background px-2 py-1 text-[10px] font-semibold text-muted-foreground sm:inline-flex">
            {item.shortcutHint}
          </kbd>
        ) : null}
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
      {onTogglePin ? (
        <button
          type="button"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-muted-foreground opacity-100 transition hover:bg-background hover:text-primary sm:opacity-0 sm:group-hover:opacity-100"
          aria-label={item.pinned ? `Hiq ${item.label} nga favorites` : `Shto ${item.label} në favorites`}
          onClick={onTogglePin}
        >
          {item.pinned ? <PinOff className="h-4 w-4" aria-hidden="true" /> : <Pin className="h-4 w-4" aria-hidden="true" />}
        </button>
      ) : null}
      {onRemoveRecent ? (
        <button
          type="button"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-muted-foreground opacity-100 transition hover:bg-background hover:text-destructive sm:opacity-0 sm:group-hover:opacity-100"
          aria-label={`Hiq ${item.label} nga recent`}
          title="Hiq nga recent"
          onClick={onRemoveRecent}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function HighlightedText({ value, query }: { value: string; query: string }): JSX.Element {
  if (!query.trim()) {
    return <>{value}</>;
  }

  const matchIndex = value.toLowerCase().indexOf(query.trim().toLowerCase());
  if (matchIndex < 0) {
    return <>{value}</>;
  }

  const before = value.slice(0, matchIndex);
  const match = value.slice(matchIndex, matchIndex + query.length);
  const after = value.slice(matchIndex + query.length);

  return (
    <>
      {before}
      <mark className="rounded bg-primary/15 px-0.5 text-primary">{match}</mark>
      {after}
    </>
  );
}

function SmartSearchSkeleton(): JSX.Element {
  return (
    <div className="grid gap-2" aria-label="Duke ngarkuar rezultatet">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="flex animate-pulse items-center gap-3 rounded-2xl bg-secondary/40 p-3">
          <div className="h-10 w-10 rounded-2xl bg-background" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-background" />
            <div className="h-3 w-2/3 rounded bg-background" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SmartSearchEmptyState(): JSX.Element {
  return (
    <div className="grid min-h-64 place-items-center rounded-3xl border border-dashed border-border bg-secondary/30 p-6 text-center">
      <div>
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary">
          <FileSearch className="h-5 w-5" aria-hidden="true" />
        </div>
        <p className="text-sm font-semibold text-card-foreground">Nuk u gjet asnjë rezultat</p>
        <p className="mt-1 max-w-sm text-xs text-muted-foreground">
          Provo emrin e faqes, një modul si “Mesazhet”, ose përdor navigimin anësor.
        </p>
      </div>
    </div>
  );
}

function buildBackendGroups(results: SmartSearchResult[]): PaletteGroup[] {
  const groupedResults = new Map<string, PaletteItem[]>();
  results.forEach((result) => {
    const group = backendGroupForResult(result);
    const items = groupedResults.get(group.id) ?? [];
    items.push(backendResultToPaletteItem(result));
    groupedResults.set(group.id, items);
  });

  return BACKEND_GROUP_ORDER.map((group) => ({
    id: group.id,
    label: group.label,
    items: groupedResults.get(group.id) ?? [],
  })).filter((group) => group.items.length > 0);
}

function backendResultToPaletteItem(result: SmartSearchResult): PaletteItem {
  const subtitleParts = [
    result.subtitle,
    result.snippet,
    result.description,
  ].filter((part): part is string => typeof part === "string" && part.trim().length > 0);

  return {
    id: `backend:${result.source_module}:${result.type}:${result.id}`,
    type: "backend",
    label: result.title,
    route: result.route,
    subtitle: subtitleParts[0] ?? backendGroupForResult(result).label,
    icon: backendIconForResult(result),
    shortcutHint: "Enter",
    backendResult: result,
  };
}

function backendGroupForResult(result: SmartSearchResult): { id: string; label: string } {
  if (result.type === "action" || result.source_module === "actions") {
    return { id: "backend-actions", label: "Actions" };
  }
  if (result.type === "appointment" || result.source_module === "appointments") {
    return { id: "backend-appointments", label: "Appointments" };
  }
  if (["person", "doctor", "patient", "staff"].includes(result.type) || result.source_module === "people") {
    return { id: "backend-people", label: "People" };
  }
  if (result.type === "message" || result.source_module === "messages") {
    return { id: "backend-messages", label: "Messages" };
  }
  if (
    ["report", "report_template", "diagnosis", "medication", "episode"].includes(result.type) ||
    result.source_module === "reports"
  ) {
    return { id: "backend-reports", label: "Reports" };
  }
  if (["business", "department", "service"].includes(result.type) || result.source_module === "business") {
    return { id: "backend-business", label: "Business" };
  }
  if (
    ["marketplace", "product", "cart", "favorite", "order"].includes(result.type) ||
    result.source_module === "marketplace"
  ) {
    return { id: "backend-marketplace", label: "Marketplace" };
  }
  return { id: "backend-modules", label: "Modules" };
}

function backendIconForResult(result: SmartSearchResult): ReactNode {
  if (result.type === "action") {
    return <Sparkles className="h-4 w-4" aria-hidden="true" />;
  }
  if (result.type === "appointment") {
    return <CalendarDays className="h-4 w-4" aria-hidden="true" />;
  }
  if (["person", "doctor", "patient", "staff"].includes(result.type)) {
    return <UserRound className="h-4 w-4" aria-hidden="true" />;
  }
  if (result.type === "message") {
    return <MessageCircle className="h-4 w-4" aria-hidden="true" />;
  }
  if (["report", "report_template", "diagnosis", "medication", "episode"].includes(result.type)) {
    return <FileText className="h-4 w-4" aria-hidden="true" />;
  }
  if (["business", "department"].includes(result.type)) {
    return <Building2 className="h-4 w-4" aria-hidden="true" />;
  }
  if (result.type === "service") {
    return <Stethoscope className="h-4 w-4" aria-hidden="true" />;
  }
  if (["marketplace", "product", "cart", "favorite", "order"].includes(result.type)) {
    return <Package className="h-4 w-4" aria-hidden="true" />;
  }
  if (result.source_module === "admin") {
    return <Users className="h-4 w-4" aria-hidden="true" />;
  }
  return <LayoutDashboard className="h-4 w-4" aria-hidden="true" />;
}

const BACKEND_GROUP_ORDER = [
  { id: "backend-actions", label: "Actions" },
  { id: "backend-appointments", label: "Appointments" },
  { id: "backend-people", label: "People" },
  { id: "backend-messages", label: "Messages" },
  { id: "backend-reports", label: "Reports" },
  { id: "backend-business", label: "Business" },
  { id: "backend-marketplace", label: "Marketplace" },
  { id: "backend-modules", label: "Modules" },
] as const;

function storedPageToPaletteItem(
  storedPage: StoredPage,
  type: Extract<PaletteItemType, "favorite" | "recent_page">,
  visiblePage?: PageSearchResult,
): PaletteItem {
  const page = visiblePage ?? storedPage;
  return {
    id: `${type.replace("_", "-")}:${storedPage.id}`,
    type,
    label: page.label,
    route: page.route,
    subtitle: type === "favorite" ? "Pinned page" : "Faqe e vizituar së fundmi",
    icon: visiblePage?.icon ?? <Clock3 className="h-4 w-4" aria-hidden="true" />,
    shortcutHint: "Enter",
    pinned: type === "favorite",
  };
}

function itemMatchesQuery(item: PaletteItem, normalizedQuery: string): boolean {
  if (!normalizedQuery) {
    return true;
  }

  return `${item.label} ${item.routePreview ?? ""}`.toLowerCase().includes(normalizedQuery);
}

function readStringListFromStorage(key: string): string[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function writeStringListToStorage(key: string, value: string[]): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
}

function readStoredPagesFromStorage(key: string): StoredPage[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is StoredPage => (
      typeof item?.id === "string" &&
      typeof item?.label === "string" &&
      typeof item?.route === "string"
    ));
  } catch {
    return [];
  }
}

function writeStoredPagesToStorage(key: string, value: StoredPage[]): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
}
